import { type IReactionDisposer, makeAutoObservable, reaction, runInAction } from 'mobx'
import { TableStore } from 'shared/ui/TableNew'
import { logger } from 'shared/lib'
import { RequestLoadData } from 'shared/api/lib'
import type {
  IAnalyticsParams,
  IParamsCreditUsageAnalyticsList,
  IResponseCreditUsageAnalyticsDetailsItem,
} from 'entities/Analytics/api/types'
import { CreditUsage } from 'entities/Analytics/model/CreditUsage'
import { CreditUsageAnalyticsApi } from 'entities/Analytics/api/creditUsageAnalytics'
import { RowCreditUsage } from 'entities/Analytics/model/Table/RowCreditUsage'
import { type IEntitiesStore } from 'entities/EntitiesExport'
import { tableLimitStore } from 'entities/TableLimit'
import type { IPaginationSourceType } from 'entities/Users/api/types'
import { Message } from 'entities/Message/model/Message'

export type TabType = 'messages' | 'calls' | 'chatbots' | 'ai_calling_agent' | 'transcripts'

export class CreditUsageTableStore {
  private readonly _tableStore: TableStore<RowCreditUsage>
  private _activeTab: TabType = 'messages'
  private _itemsMap: Map<number, CreditUsage> = new Map()
  private _messagesMap: Map<number, Message> = new Map()
  private _firstLoading = true
  private _disposeParamsWithoutPage: IReactionDisposer | null = null
  private _disposeParamsGetItems: IReactionDisposer | null = null
  private _disposeAnalyticsFiltersParams: IReactionDisposer | null = null

  constructor(
    private _analyticsFiltersParams: () => IAnalyticsParams,
    private _entities: IEntitiesStore
  ) {
    this._tableStore = new TableStore({
      key: 'creditUsageTable',
      element: 'creditUsage',
      limit: 10,
      getLimit: () => tableLimitStore.getLimit(this.tabKey),
      setLimit: (value) => tableLimitStore.setLimit(this.tabKey, value),
    })
    makeAutoObservable(this)
  }

  init = () => {
    this.loadData()
    this.reactionParamsWithoutPage()
    this.reactionParamsGetItems()
    this.reactionAnalyticsFiltersParams()
    return this.dispose
  }

  dispose = () => {
    this._disposeParamsWithoutPage?.()
    this._disposeParamsGetItems?.()
    this._disposeAnalyticsFiltersParams?.()
    this._requestLoadData.dispose()
  }

  reactionParamsWithoutPage = () => {
    this._disposeParamsWithoutPage?.()
    this._disposeParamsWithoutPage = reaction(
      () => this.paramsWithoutPage,
      () => {
        this._tableStore.setLoading(true)
        this._tableStore.setPage(1)
      }
    )
  }

  reactionParamsGetItems = () => {
    this._disposeParamsGetItems?.()
    this._disposeParamsGetItems = reaction(
      () => this.paramsGetItems,
      () => this._requestLoadData.debounceLoadData()
    )
  }

  reactionAnalyticsFiltersParams = () => {
    this._disposeAnalyticsFiltersParams?.()
    this._disposeAnalyticsFiltersParams = reaction(
      () => this._analyticsFiltersParams(),
      () => {
        this._tableStore.setLimit(this.cachedLimit)
      }
    )
  }

  private readonly _tabKeyMap: Record<TabType, IPaginationSourceType> = {
    messages: 'credit-usage-messages-table',
    calls: 'credit-usage-calls-table',
    chatbots: 'credit-usage-chatbots-table',
    ai_calling_agent: 'credit-usage-ai-calling-agent-table',
    transcripts: 'credit-usage-transcripts-table',
  }

  get tabKey(): IPaginationSourceType {
    return this._tabKeyMap[this._activeTab]
  }

  get cachedLimit() {
    return tableLimitStore.getLimit(this.tabKey) ?? 10
  }

  get items() {
    return Array.from(this._itemsMap.values())
  }

  getMessage = (id: number): Message | undefined => {
    return this._messagesMap.get(id)
  }

  get itemsRows() {
    return this.items.map((item) => new RowCreditUsage(item))
  }

  get activeTab() {
    return this._activeTab
  }

  get tableStore() {
    return this._tableStore
  }

  get firstLoading() {
    return this._firstLoading
  }

  get loading() {
    return this._tableStore.loading
  }

  get paramsWithoutPage(): Omit<IParamsCreditUsageAnalyticsList, 'page'> {
    return {
      ...this._analyticsFiltersParams(),
      limit: this._tableStore.limit,
      object_event_type_group: this._activeTab,
    }
  }

  get paramsGetItems(): IParamsCreditUsageAnalyticsList {
    return {
      page: this._tableStore.page,
      ...this.paramsWithoutPage,
    }
  }

  private _requestLoadData = new RequestLoadData({
    requestCallback: async () => {
      runInAction(() => {
        this._tableStore.setLoading(true)
        this._itemsMap.clear()
        this._messagesMap.clear()
      })

      const listResponse = await CreditUsageAnalyticsApi.getCreditUsageAnalyticsList(
        this.paramsGetItems
      )
      const detailsResponse = await CreditUsageAnalyticsApi.getCreditUsageAnalyticsDetails(
        listResponse.data.data
      )

      if (this._activeTab === 'messages' || this._activeTab === 'chatbots') {
        const newContactIds = new Set<number>()
        const allContactIds = new Set<number>()

        detailsResponse.data.forEach((item) => {
          const contactId = item.details?.message?.contact_id
          if (contactId) {
            allContactIds.add(contactId)
            if (!this._entities.contactsStore.hasItem(contactId)) {
              newContactIds.add(contactId)
            }
          }
        })

        if (newContactIds.size > 0) {
          const contacts = await this._entities.contactsStore.getByIds(Array.from(newContactIds))

          if (contacts && contacts.length > 0) {
            contacts.forEach((contact) => {
              this._entities.contactsStore.itemsMap.set(contact.id, contact)
            })
          }
        }
      }

      runInAction(() => {
        detailsResponse.data.forEach((item) => {
          if (item.id) {
            this.setItem(item)
          }
        })
        this._tableStore.setTotal(listResponse.data.meta.total)
      })
    },
    finallyCallback: () => {
      runInAction(() => {
        this._tableStore.setLoading(false)
        this._firstLoading = false
      })
    },
  })

  loadData = async () => {
    return this._requestLoadData.loadData()
  }

  setItem = (data: IResponseCreditUsageAnalyticsDetailsItem) => {
    runInAction(() => {
      const creditUsage = new CreditUsage(data)
      this._itemsMap.set(data.id, creditUsage)

      const messageData = creditUsage.details?.message

      if (messageData) {
        try {
          const message = new Message(
            {
              message: messageData,
            },
            this._entities
          )
          this._messagesMap.set(data.id, message)
          this._entities.attachmentStore.checkPreviousAttachment(message.links)
        } catch (error) {
          logger.error(error)
        }
      }
    })
  }

  setActiveTab = (tab: TabType) => {
    this._activeTab = tab
    this._tableStore.setLimit(this.cachedLimit)
  }
}
